import PropTypes from 'prop-types';
import { Box, Skeleton, Typography } from '@mui/material';
import IRDataGrid from 'features/IRWorkstation/components/Common/IRDataGrid';

// Shared workstation table card: title bar with record count, optional filter bar,
// error state, and the grid (fixed headers, server-side sort, infinite scroll).
// `height` sets the card height; omitted, it sizes to its rows.
const TableCard = ({
	title,
	totalCount,
	totalLabel = 'records',
	filterBar = null,
	columns,
	rows,
	loading,
	loadingMore = false,
	hasMore = false,
	error = null,
	errorMessage = 'Unable to load records',
	sortModel,
	onSortModelChange,
	onRowsScrollEnd,
	noRowsMessage,
	height,
	dataTestId = 'data-grid',
	cardTestId,
}) => (
	<Box
		data-testid={cardTestId}
		sx={{
			width: '100%',
			minWidth: 0,
			backgroundColor: 'background.paper',
			border: '0.5px solid',
			borderColor: 'border.light',
			borderRadius: '12px',
			overflow: 'hidden',
			...(height
				? { height, display: 'flex', flexDirection: 'column' }
				: { flexShrink: 0 }),
		}}
	>
		<Box
			sx={{
				padding: '10px 14px',
				borderBottom: '0.5px solid',
				borderColor: 'border.light',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'space-between',
				gap: '14px',
			}}
		>
			{title ? (
				<Typography sx={{ fontSize: '12px', fontWeight: 600 }}>{title}</Typography>
			) : (
				// keeps the record count pinned right via space-between when there's no title
				<Box />
			)}
			{loading ? (
				<Skeleton variant='text' width={100} height={16} />
			) : (
				totalCount != null && (
					<Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
						{totalCount} {totalLabel}
					</Typography>
				)
			)}
		</Box>
		{filterBar}
		{error ? (
			<Box
				sx={{
					minHeight: 200,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
				}}
			>
				<Typography color='error' sx={{ fontSize: '12px' }}>
					{errorMessage}
				</Typography>
			</Box>
		) : (
			<Box sx={height ? { flex: 1, minHeight: 0 } : undefined}>
				<IRDataGrid
					rows={rows}
					columns={columns}
					loading={loading}
					loadingMore={loadingMore}
					hasMore={hasMore}
					sortModel={sortModel}
					onSortModelChange={onSortModelChange}
					onRowsScrollEnd={onRowsScrollEnd}
					noRowsMessage={noRowsMessage}
					dataTestId={dataTestId}
					{...(height
						? { minHeight: 0, maxHeight: '100%', fillHeight: true }
						: {})}
				/>
			</Box>
		)}
	</Box>
);

TableCard.propTypes = {
	title: PropTypes.string,
	totalCount: PropTypes.number,
	totalLabel: PropTypes.string,
	filterBar: PropTypes.node,
	columns: PropTypes.arrayOf(PropTypes.object).isRequired,
	rows: PropTypes.arrayOf(PropTypes.object).isRequired,
	loading: PropTypes.bool,
	loadingMore: PropTypes.bool,
	hasMore: PropTypes.bool,
	error: PropTypes.string,
	errorMessage: PropTypes.string,
	sortModel: PropTypes.arrayOf(
		PropTypes.shape({
			field: PropTypes.string,
			sort: PropTypes.oneOf(['asc', 'desc']),
		})
	),
	onSortModelChange: PropTypes.func,
	onRowsScrollEnd: PropTypes.func,
	noRowsMessage: PropTypes.string,
	height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	dataTestId: PropTypes.string,
	cardTestId: PropTypes.string,
};

export default TableCard;
